import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ownerSelector } from "../../store/store";
import { PAYMENT_URL } from "../../constants";
import { toast } from "react-toastify";
import { refreshBillingSession } from "./billingSession";
import { Button } from "../ui";
import { resolveBillingSource } from "../../utils/billingSource";
import { resolvePlanAmountDisplay } from "../../utils/seats";

type Props = {
  isLaunchFreeBilling?: boolean;
  launchTrialLabel?: string;
  growthPlanName?: string;
};

const getCalendarDaysRemaining = (endDate: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDay = new Date(endDate);
  endDay.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((endDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
};

const BillingOverview = ({
  isLaunchFreeBilling = false,
  launchTrialLabel = "7-day free trial",
  growthPlanName = "Innlead",
}: Props) => {
  const { owner, currentUser } = useSelector(ownerSelector);
  const dispatch = useDispatch();
  const billing = resolveBillingSource({ owner, currentUser });
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [dayAnchor, setDayAnchor] = useState(() => new Date().toDateString());

  useEffect(() => {
    const now = new Date();
    const nextMidnight = new Date(now);
    nextMidnight.setDate(nextMidnight.getDate() + 1);
    nextMidnight.setHours(0, 0, 0, 0);
    const timeoutId = window.setTimeout(() => {
      setDayAnchor(new Date().toDateString());
    }, nextMidnight.getTime() - now.getTime());
    return () => window.clearTimeout(timeoutId);
  }, [dayAnchor]);
  const billingHistory = Array.isArray((billing as any)?.planhistory)
    ? (billing as any).planhistory
    : [];
  const planhistory = billingHistory[billingHistory.length - 1];
  const amountDisplay = useMemo(
    () => resolvePlanAmountDisplay((planhistory as any) || {}),
    [planhistory]
  );
  const chargedAt = planhistory?.chargedAt
    ? new Date(planhistory.chargedAt.toString())
    : undefined;
  const parseDate = (raw: any) => {
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const periodEnd = parseDate((planhistory as any)?.periodEndDate);
  const trialEnd = parseDate((planhistory as any)?.trialEndDate);
  const trialStart = parseDate((planhistory as any)?.trialStartDate);
  const planStart = parseDate((planhistory as any)?.planStartDate);
  const effectiveEndDate =
    periodEnd && trialEnd
      ? new Date(Math.max(periodEnd.getTime(), trialEnd.getTime()))
      : periodEnd || trialEnd;
  const isTrialPlan = Boolean((planhistory as any)?.isTrial);

  const isCancelled = Boolean((planhistory as any)?.isCancelled);
  let validTillText: string | null = null;
  if (isCancelled) {
    const now = new Date();
    if (effectiveEndDate) {
      validTillText = effectiveEndDate < now ? "Expired" : effectiveEndDate.toLocaleDateString();
    } else if (chargedAt && !Number.isNaN(chargedAt.getTime())) {
      const validTillDate = new Date(chargedAt.getTime());
      validTillDate.setDate(validTillDate.getDate() + 30);
      validTillText =
        validTillDate < now ? "Expired" : validTillDate.toLocaleDateString();
    }
  }
  const dateTimeOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const formatDateTime = (value: Date | null | undefined) =>
    value && !Number.isNaN(value.getTime()) ? value.toLocaleString("en-US", dateTimeOptions) : "--";
  const formatDate = (value: Date | null | undefined) =>
    value && !Number.isNaN(value.getTime()) ? value.toLocaleDateString("en-US", dateOptions) : "--";

  const hasAnyPlan = billingHistory.length > 0;
  const isPlanActive = (() => {
    if (!hasAnyPlan || !planhistory) return false;
    const now = new Date();
    if (effectiveEndDate) {
      if (effectiveEndDate < now) return false;
    }
    if (Boolean((planhistory as any)?.isCancelled)) return false;
    return Boolean((planhistory as any)?.isActive);
  })();

  const prettyPlan = (() => {
    const name = String((planhistory as any)?.planName || "").toLowerCase();
    const pid = String((planhistory as any)?.planId || "").toLowerCase();
    const inferredKey =
      name.includes("professional") || pid.includes("professional")
        ? "professional"
        : name.includes("growth") ||
          name.includes("premium") ||
          pid.includes("growth") ||
          pid.includes("premium")
        ? "growth"
        : "";
    const inferredInterval = name.includes("year")
      ? "yearly"
      : name.includes("quarter")
      ? "quarterly"
      : "monthly";
    const key =
      String((planhistory as any)?.planKey || "").toLowerCase() || inferredKey;
    const interval =
      String((planhistory as any)?.interval || "").toLowerCase() || inferredInterval;
    if (key === "growth" || key === "professional") {
      const planTitle = key === "growth" ? growthPlanName : "Professional";
      return interval ? `${planTitle} (${interval})` : planTitle;
    }
    return hasAnyPlan ? (planhistory?.planName || "Unknown Plan") : "No active plan";
  })();

  const isOnActiveTrial = isTrialPlan && isPlanActive;
  const trialStartedAt = trialStart || planStart || chargedAt || null;
  const trialEndsAt = isTrialPlan ? effectiveEndDate : null;
  const paidPlanStartedAt = planStart || chargedAt || null;

  const trialDaysRemaining = useMemo(() => {
    if (!isOnActiveTrial || !trialEndsAt) return null;
    return getCalendarDaysRemaining(trialEndsAt);
  }, [isOnActiveTrial, trialEndsAt, dayAnchor]);

  const trialDaysRemainingLabel =
    trialDaysRemaining == null
      ? null
      : trialDaysRemaining === 0
      ? isLaunchFreeBilling
        ? "Free period ends today"
        : "Trial ends today"
      : `${trialDaysRemaining} day${trialDaysRemaining === 1 ? "" : "s"} remaining`;

  const planLabel = isOnActiveTrial
    ? isLaunchFreeBilling
      ? `${prettyPlan} — Early joiner`
      : `${prettyPlan} — Trial`
    : prettyPlan;

  const amountLabel = (() => {
    if (!hasAnyPlan) return "$ 0";
    const symbol = planhistory?.currencySymbol || "$";
    const price = `${symbol} ${amountDisplay.amount}${amountDisplay.suffix}`;
    if (isOnActiveTrial) {
      return isLaunchFreeBilling ? "No payment during free period" : "No charge during trial";
    }
    return price;
  })();

  const amountSubLabel =
    isOnActiveTrial && hasAnyPlan
      ? `Then ${planhistory?.currencySymbol || "$"} ${amountDisplay.amount}${amountDisplay.suffix}`
      : null;

  const hasPaymentFailed = (() => {
    const tries = Number((billing as any)?.PaymentTries ?? 0);
    const last = planhistory as any;
    return (
      tries >= 1 &&
      Boolean(last?.planId) &&
      last?.planId !== "FREE_PLAN" &&
      last?.isActive === false &&
      !Boolean(last?.isCancelled)
    );
  })();

  const canCancelCurrent =
    (() => {
      const resolvedUserId =
        (typeof currentUser?.userId === "string" && currentUser.userId) ||
        (typeof (owner as any)?.userId === "string" && (owner as any).userId) ||
        "";
      const currentSubscriptionId = String((planhistory as any)?.subscriptionId || "");
      const hasCancellationScheduledForCurrent = Array.isArray((billing as any)?.planhistory)
        ? (billing as any)?.planhistory?.some(
            (p: any) =>
              String(p?.subscriptionId || "") === currentSubscriptionId &&
              Boolean(p?.isCancelled)
          ) || false
        : false;
      return (
        Boolean(owner?._id || resolvedUserId) &&
        Boolean(currentSubscriptionId) &&
        !hasCancellationScheduledForCurrent &&
        !isLaunchFreeBilling &&
        !hasPaymentFailed
      );
    })();

  const updatePaymentAndPay = async () => {
    const resolvedUserId =
      (typeof currentUser?.userId === "string" && currentUser.userId) ||
      (typeof (owner as any)?.userId === "string" && (owner as any).userId) ||
      "";
    if ((!owner?._id && !resolvedUserId) || isUpdatingPayment) return;
    try {
      setIsUpdatingPayment(true);
      const startCheckout = async (endpoint: string) => {
        const resp = await fetch(`${PAYMENT_URL}/${endpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ownerId: owner?._id,
            userId: resolvedUserId || undefined,
          }),
        });
        const data = await resp.json();
        return { resp, data };
      };

      let { resp, data } = await startCheckout("retryInvoice");
      if (
        !resp.ok &&
        (data?.code === "RESUBSCRIBE_REQUIRED" ||
          String(data?.error || "").toLowerCase().includes("resubscrib"))
      ) {
        ({ resp, data } = await startCheckout("resubscribe"));
      }
      if (!resp.ok) {
        toast.error(data?.error || "Unable to start payment. Please try again.");
        return;
      }
      if (data?.url) {
        if (/external\/error|does not validate/i.test(String(data.url))) {
          toast.error("Checkout session invalid. Please try again.");
          return;
        }
        window.location.href = data.url;
      } else {
        toast.error("Unable to start payment. Please try again.");
      }
    } catch (e: any) {
      toast.error(e?.message || "Unable to start payment. Please try again.");
    } finally {
      setIsUpdatingPayment(false);
    }
  };

  const cancelCurrentSubscription = async () => {
    const resolvedUserId =
      (typeof currentUser?.userId === "string" && currentUser.userId) ||
      (typeof (owner as any)?.userId === "string" && (owner as any).userId) ||
      "";
    if ((!owner?._id && !resolvedUserId) || !canCancelCurrent || isCancelling) return;
    try {
      setIsCancelling(true);
      const resp = await fetch(`${PAYMENT_URL}/cancelSubscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownerId: owner?._id, userId: resolvedUserId }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data?.error || "Unable to cancel subscription. Please try again.");
        return;
      }
      toast.success("Subscription will be cancelled at period end.");
      // Refresh billing state so the UI immediately hides the "Cancel" option
      try {
        await refreshBillingSession({ owner, currentUser, dispatch });
      } catch (e) {
        console.warn("[BillingOverview] Failed to refresh billing after cancellation", e);
      }
    } catch (e: any) {
      toast.error(e?.message || "Unable to cancel subscription. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="bg-white relative p-3 rounded-[8px] text-xs flex flex-col gap-3 border border-light-grey shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      {showCancelConfirm ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md min-w-0 p-6 relative">
            <button
              type="button"
              aria-label="Close"
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              onClick={() => {
                if (isCancelling) return;
                setShowCancelConfirm(false);
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M1 1l12 12M13 1L1 13" />
              </svg>
            </button>
            <p className="text-lg font-semibold pr-8">Cancel your plan?</p>
            <p className="text-sm text-gray-600 mt-3">
              Your plan will remain active until the end of the current period.
            </p>
            <div className="flex gap-3 justify-end mt-6">
              <button
                disabled={isCancelling}
                className="border border-gray-300 text-gray-700 text-sm px-4 py-2.5 rounded-md hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => {
                  if (isCancelling) return;
                  setShowCancelConfirm(false);
                }}
              >
                No
              </button>
              <button
                disabled={isCancelling}
                className="bg-red-500 text-white text-sm px-4 py-2.5 rounded-md hover:bg-red-600 disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={async () => {
                  if (isCancelling) return;
                  await cancelCurrentSubscription();
                  setShowCancelConfirm(false);
                }}
              >
                {isCancelling ? "Cancelling..." : "Yes, cancel"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-[16px] font-semibold text-dark-black">Billing Overview</h1>
        <div className="flex flex-col items-end gap-1">
          {isOnActiveTrial ? (
            <div
              className="inline-flex flex-col items-stretch overflow-hidden rounded-[8px] border border-brand-blue/30 bg-bg-light-blue shadow-[0_1px_2px_rgba(92,122,247,0.12)]"
              role="status"
              aria-label={`${isLaunchFreeBilling ? "Early joiner period active" : "Trial active"}. ${trialDaysRemainingLabel || ""}`}
            >
              <span className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-brand-blue">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-blue" aria-hidden />
                {isLaunchFreeBilling ? "Early joiner active" : "Trial active"}
              </span>
              {trialDaysRemainingLabel ? (
                <span className="border-t border-brand-blue/15 bg-white/50 px-2.5 py-0.5 text-center text-[10px] font-medium text-brand-blue/90">
                  {trialDaysRemainingLabel}
                </span>
              ) : null}
            </div>
          ) : hasPaymentFailed ? (
            <span
              className="inline-flex items-center rounded-[6px] bg-red-400/30 px-2.5 py-1 text-[11px] font-semibold text-red-800"
              role="status"
            >
              Payment failed
            </span>
          ) : isPlanActive ? (
            <span
              className="inline-flex items-center rounded-[6px] bg-green-400/30 px-2.5 py-1 text-[11px] font-semibold text-green-800"
              role="status"
            >
              Active
            </span>
          ) : (
            <span
              className="inline-flex items-center rounded-[6px] bg-red-400/30 px-2.5 py-1 text-[11px] font-semibold text-red-800"
              role="status"
            >
              {hasAnyPlan
                ? isTrialPlan
                  ? isLaunchFreeBilling
                    ? "Free period ended"
                    : "Trial expired"
                  : "Inactive"
                : "No plan"}
            </span>
          )}
          {validTillText && (
            <p className="text-[10px] text-gray-600">
              Valid till: <span className="font-semibold">{validTillText}</span>
            </p>
          )}
        </div>
      </div>

      <div
        className={[
          "grid grid-cols-1 gap-2.5",
          isOnActiveTrial ? "md:grid-cols-2 xl:grid-cols-4" : "md:grid-cols-3",
        ].join(" ")}
      >
        <div className="rounded-[6px] border border-light-grey bg-background-grey px-3 py-2">
          <p className="text-[10px] uppercase tracking-wide text-dark-grey/80">Plan</p>
          <p className="text-[12px] text-mainColor font-semibold mt-0.5">{planLabel}</p>
        </div>

        {isOnActiveTrial ? (
          <>
            <div className="rounded-[6px] border border-light-grey bg-background-grey px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-dark-grey/80">
                {isLaunchFreeBilling ? "Free period started" : "Trial started"}
              </p>
              <p className="text-[12px] text-mainColor font-semibold mt-0.5">
                {hasAnyPlan ? formatDateTime(trialStartedAt) : "--"}
              </p>
            </div>
            <div className="rounded-[6px] border border-light-grey bg-background-grey px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-dark-grey/80">
                {isLaunchFreeBilling ? "Free period ends" : "Trial ends"}
              </p>
              <p className="text-[12px] text-mainColor font-semibold mt-0.5">
                {hasAnyPlan ? formatDate(trialEndsAt) : "--"}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-[6px] border border-light-grey bg-background-grey px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-dark-grey/80">Plan started</p>
              <p className="text-[12px] text-mainColor font-semibold mt-0.5">
                {hasAnyPlan ? formatDateTime(paidPlanStartedAt) : "--"}
              </p>
            </div>
            <div className="rounded-[6px] border border-light-grey bg-background-grey px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-dark-grey/80">
                {isCancelled ? "Valid until" : "Renews on"}
              </p>
              <p className="text-[12px] text-mainColor font-semibold mt-0.5">
                {hasAnyPlan && periodEnd ? formatDate(periodEnd) : "--"}
              </p>
            </div>
          </>
        )}

        <div className="rounded-[6px] border border-light-grey bg-background-grey px-3 py-2">
          <p className="text-[10px] uppercase tracking-wide text-dark-grey/80">Amount</p>
          <p className="text-[12px] text-mainColor font-semibold mt-0.5">{amountLabel}</p>
          {amountSubLabel ? (
            <p className="text-[10px] text-dark-grey mt-0.5">{amountSubLabel}</p>
          ) : null}
        </div>
      </div>
      {hasPaymentFailed ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-[6px] border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-[12px] text-red-800">
            We couldn&apos;t renew your plan. Update your card and pay to restore access.
          </p>
          <Button
            variant="primary"
            size="sm"
            onClick={updatePaymentAndPay}
            isDisabled={isUpdatingPayment}
          >
            {isUpdatingPayment ? "Opening checkout..." : "Update payment method & pay"}
          </Button>
        </div>
      ) : null}
      {canCancelCurrent ? (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setShowCancelConfirm(true)} isDisabled={isCancelling}>
            {isCancelling ? "Cancelling..." : "Cancel subscription"}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default BillingOverview;
